import { Box } from "@chakra-ui/react";
import {
  BaseButton,
  baseColors,
  BaseStack,
  call,
  ColorIcon,
  customEvent,
  Spacing,
} from "goi_common";
import { TutorialCarousel } from "goi_common/src/design-system";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";

import { JangjiResultCell } from "@/utils/jangjiCell";
import { useHideCallCta } from "@/hooks/useHideCallCta";

import { JANGJI_STEP_BODIES } from "./JangjiStepBodies";

/**
 * 장지 견적 결과 화면 스텝 가이드 (실험 #336 B안).
 *
 * 기존 결과 화면(배너 + 견적서 아코디언 + 상담 안내)을 6스텝으로 교체한다.
 *
 *   0  견적 준비 중
 *   1  장지 이해        공설과 사설이 무엇이 다른가
 *   2  공설: 지금 선택   / 사설: 자리 조건
 *   3  공설: 시설       / 사설: 되돌리기
 *   4  공설: 상조       / 사설: 전체 장례
 *   5  전체 비용 확인
 *
 * ── 왜 스텝인가
 *
 * 실험 #324(O/X 퀴즈)가 이 화면에서 콘텐츠를 늘렸다가 goi_call 41.0% -> 12.8% 로 졌다.
 * 같은 실험에서 네비바 장례견적 진입은 5.1% -> 14.1% 로 **올랐다.** 장례 탐색이 준 게 아니라
 * 전화 버튼이 콘텐츠에 밀린 것으로 읽힌다.
 *
 * 스텝 구조가 그 문제를 구조적으로 없앤다. 한 화면에 한 스텝만 있으니 콘텐츠가 아무리 길어도
 * 상담 진입이 접히지 않는다. 그리고 **상담 연결 플로팅 버튼이 모든 스텝의 하단 버튼 위에 있다.**
 * 이 두 가지가 이 화면의 설계 전제다. 스텝을 늘리든 줄이든 그 둘은 유지할 것.
 *
 * ── 셸을 직접 만들지 않고 TutorialCarousel 을 쓴다
 *
 * `goi_common/design-system/composites/TutorialCarousel` 이 이미 캐러셀과 dot,
 * 하단 고정 CTA(그라데이션 포함), 480px 폭 제한을 다 갖고 있다. 디자인 시안의 프레임을
 * 손으로 다시 만들면 그 컴포넌트와 스타일이 갈라진다.
 *
 * 시안의 상단 "고이 + 상담원 연결하기" 헤더는 목업이 플랫폼 헤더를 대신 그린 것이다. 실제
 * 페이지에는 이미 전역 헤더가 있어서 로고를 또 그리면 두 개가 된다. 그래서 로고는 빼고,
 * 상담 연결은 상단 텍스트 바 대신 하단 플로팅 버튼으로 내렸다(상단에서는 눈에 안 들어왔다).
 */

/** 스텝 수. dot 개수는 TutorialCarousel 이 CarouselItems 길이로 알아서 그린다 */
const STEP_COUNT = 6;

/**
 * "매달 1만 명 이상" 의 근거.
 *
 * `consult_sangjoquotationrequest`(장례 견적 제출, 비회원 포함) 월별 실측이다.
 *   2026-05  13,235   2026-06  17,059   2026-07  13,972   2026-08  9,018(21일까지)
 *
 * 완결된 세 달이 모두 13,000 을 넘고 8월도 같은 페이스라 "10,000명 이상" 은 여유가 크다.
 * 정확한 수를 박으면 매달 흐르므로 넉넉한 하한을 문구로 쓴다. 견적류 리드(회원가입 완료,
 * 월 1,900~3,160)가 아니라 제출 기준인 이유는 문구가 "알아보고 계세요" 이기 때문이다.
 * 제출이 곧 알아본 행동이고, 로그인까지 끝낸 사람만 세면 오히려 과소 집계가 된다.
 */
const MONTHLY_QUOTE_FLOOR = "10,000명";

/**
 * 하단 고정 영역 위 구분선.
 *
 * TutorialCarousel 의 버튼 컨테이너가 좌우 20px padding 을 갖고 있어서, 그 안에서 borderTop
 * 을 주면 선이 화면을 꽉 채우지 않고 안쪽으로 들어간다. 음수 마진으로 그 padding 을 되돌린다.
 */
const FullBleedDivider = () => (
  <Box height="1px" backgroundColor={baseColors.line_normal} marginX="-20px" marginBottom="16px" />
);

export const JangjiResultStepGuide = ({
  cell,
  /** 제출한 시설명. 스텝 0 헤드라인에 그대로 들어간다 */
  facilityName,
  /** "전북 전주" 같은 장지 희망지역. 공설 스텝 2 문구에 쓴다 */
  regionLabel,
}: {
  cell: JangjiResultCell;
  facilityName?: string;
  regionLabel?: string;
}) => {
  const router = useRouter();
  const [step, setStep] = useState(0);

  const isLast = step === STEP_COUNT - 1;

  const track = useCallback((trigger: string) => {
    customEvent({ newGtm: { path: "jangji_done", trigger_and_target: trigger as never } });
  }, []);

  /**
   * 전화 연결. 이벤트는 `quickcall` 을 그대로 쓴다.
   *
   * 기존 결과 화면의 전화 CTA 와 같은 행동이고, 이 실험 판정에 goi_call 이 들어가서 이름을
   * 끊으면 A/B 를 비교할 수 없다. 컴포넌트가 바뀌어도 이벤트는 유지한다는 프로젝트 규칙과도 같다.
   */
  const handleCall = useCallback(() => {
    customEvent({
      newGtm: { path: "facility_match_recommendation", trigger_and_target: "quickcall" },
    });
    call("1666-9784");
  }, []);

  /**
   * 이 화면의 전화 CTA 2개(플로팅 버튼, 마지막 스텝 버튼)를 숨긴다.
   * 콜 유입은 크지만(goi_call 발화율 37%) 성과로 이어지지 않는다. 롤백은 useHideCallCta 참고.
   * `handleCall` 과 `quickcall` 이벤트는 남겨둔다. 롤백 시 이벤트 이름이 끊기면 안 된다.
   */
  const hideCallCta = useHideCallCta();

  /**
   * 스텝을 넘길 때 맨 위로 올린다.
   *
   * 스텝마다 길이가 달라서 아래쪽에서 "다음" 을 누르면 다음 스텝의 중간부터 보인다.
   * 헤드라인을 놓치면 그 스텝이 무슨 얘기인지 모른 채 읽기 시작한다.
   */
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const goNext = useCallback(() => {
    setStep((prev) => {
      const next = Math.min(prev + 1, STEP_COUNT - 1);
      track(`guide_next_${next}`);
      return next;
    });
    scrollToTop();
  }, [track, scrollToTop]);

  const goPrev = useCallback(() => {
    setStep((prev) => {
      const next = Math.max(prev - 1, 0);
      track(`guide_prev_${next}`);
      return next;
    });
    scrollToTop();
  }, [track, scrollToTop]);

  const bodies = useMemo(
    () => JANGJI_STEP_BODIES[cell]({ facilityName, regionLabel }),
    [cell, facilityName, regionLabel],
  );

  const items = useMemo(
    () =>
      bodies.map((body, index) => (
        // 상단 전역 메뉴바와 붙지 않게 위 여백을 주고, 좌우도 넓힌다.
        //
        // 아래 여백은 TutorialCarousel 이 주는 96px 로 부족하다. 이 화면의 하단 고정 영역은
        // 구분선과 사회적 증거, 버튼 두 개까지 들어가 그보다 높다. 부족하면 마지막 스텝
        // 본문이 dot 뒤로 들어가 겹친다.
        <Box key={index} padding="28px 24px 130px">
          {body}
        </Box>
      )),
    [bodies],
  );

  /**
   * 상담 연결 플로팅 버튼.
   *
   * 처음에는 상단 sticky 바에 "상담원 연결하기" 텍스트로 뒀는데 눈에 안 들어왔다. 하단
   * 버튼 바로 위 우측에 원형 버튼으로 올린다. 스텝을 넘기는 동안 손가락이 머무는 자리라
   * 텍스트 바보다 눌리기 쉽다.
   *
   * 아이콘은 스텝 0 의 상담원 일러스트를 그대로 쓴다. 같은 화면에서 이미 본 그림이라
   * 무엇을 하는 버튼인지 설명이 필요 없다.
   */
  const callFloatingButton = hideCallCta ? null : (
    <Box display="flex" justifyContent="flex-end" width="100%">
      <Box
        as="button"
        type="button"
        display="flex"
        alignItems="center"
        gap="8px"
        padding="6px 14px 6px 6px"
        borderRadius="9999px"
        backgroundColor={baseColors.white}
        boxShadow="0 4px 14px rgba(20,20,20,0.14)"
        border={`1px solid ${baseColors.line_normal}`}
        cursor="pointer"
        onClick={handleCall}
      >
        {/*
         * 전화 아이콘을 쓴다. 스텝 0 이 같은 상담원 일러스트를 크게 쓰고 있어서 여기도 그걸
         * 쓰면 한 화면에 같은 그림이 두 개가 된다. 그리고 이 버튼이 하는 일은 상담원 소개가
         * 아니라 전화 걸기다.
         */}
        <Box
          width="34px"
          height="34px"
          borderRadius="50%"
          backgroundColor={baseColors.orange_100}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <ColorIcon
            name="icon-call-mono"
            color={baseColors.orange_600}
            width="18px"
            height="18px"
          />
        </Box>
        <Box className="label_1_sb" color={baseColors.label_normal}>
          상담원 연결
        </Box>
      </Box>
    </Box>
  );

  return (
    <TutorialCarousel
      currentIndex={step}
      CarouselItems={items}
      FixedButtons={
        // 하단 영역 위에 구분선을 둔다. TutorialCarousel 의 그라데이션만으로는 경계가
        // 흐려서 콘텐츠와 버튼이 붙어 보인다.
        isLast ? (
          <BaseStack width="100%" spacing={0}>
            <FullBleedDivider />
            {/*
             * 마지막 스텝에는 상담 연결 플로팅 버튼을 두지 않는다. 아래 "지금 바로 문의하기"
             * 가 같은 일을 하므로 버튼이 둘이면 같은 행동을 두 번 권하는 화면이 된다.
             *
             * 사회적 증거. 수치 근거는 위 MONTHLY_QUOTE_FLOOR 주석 참고.
             * "알아보고 계세요" 로 둔 이유는 제출 기준이라 진행형이 사실에 맞기 때문이다.
             */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="6px"
              padding="10px 14px"
              borderRadius="10px"
              backgroundColor={baseColors.orange_100}
              color={baseColors.orange_700}
              wordBreak="keep-all"
            >
              {/*
               * 여러 사람을 뜻하는 icon-user-two-mono 를 쓴다. 이모지나 인물 일러스트를
               * 쓰지 않는 이유는 실제 이용자 사진으로 오인될 여지를 두지 않기 위해서다.
               */}
              <ColorIcon
                name="icon-user-two-mono"
                color={baseColors.orange_600}
                width="18px"
                height="18px"
              />
              <Box className="caption_1_r" textAlign="left">
                매달{" "}
                <Box as="span" className="caption_1_sb">
                  {MONTHLY_QUOTE_FLOOR} 이상
                </Box>
                이 장례 총 비용을 알아보고 계세요
              </Box>
            </Box>

            <Spacing gap={10} />

            <BaseButton
              type="primary"
              size="large"
              onClick={() => {
                track("guide_cta_total");
                // ?template=cost 를 붙이면 안 된다. 그 온보딩 화면이 이 스텝의 마지막과
                // 같은 카피, 같은 이미지라 사용자가 같은 화면을 두 번 본다.
                router.push("/match/request/");
              }}
            >
              <Box className="body_1_normal_m">무료로 예상 비용 확인하기</Box>
            </BaseButton>

            {hideCallCta || (
              <>
                <Spacing gap={8} />

                <BaseButton type="assistive_1" size="large" outlined onClick={handleCall}>
                  <Box className="body_1_normal_m">지금 바로 문의하기</Box>
                </BaseButton>
              </>
            )}
          </BaseStack>
        ) : (
          <BaseStack width="100%" spacing={0}>
            <FullBleedDivider />
            {callFloatingButton}
            <Spacing gap={10} />
            <BaseStack direction="horizontal" width="100%" spacing={10}>
              {/*
               * 첫 스텝에서도 "이전" 을 렌더한다. 버튼이 사라지면 "다음" 폭이 스텝마다 달라져
               * 누르는 자리가 흔들린다. step 0 에서는 눌러도 0 에 머문다.
               */}
              <Box flex={1} opacity={step === 0 ? 0.45 : 1}>
                <BaseButton type="assistive_1" size="large" outlined onClick={goPrev}>
                  <Box className="body_1_normal_m">이전</Box>
                </BaseButton>
              </Box>
              <Box flex={1.4}>
                <BaseButton type="primary" size="large" onClick={goNext}>
                  <Box className="body_1_normal_m">다음</Box>
                </BaseButton>
              </Box>
            </BaseStack>
          </BaseStack>
        )
      }
    />
  );
};
